import { useLayoutEffect, useRef, useState } from "react";
import { ButtonV2 } from "../FormControlsV2";
import { useOnboarding } from "../../hooks/useOnboarding";
import "./OnboardingFooterV2.css";
import { useTranslation } from "react-i18next";

type OnboardingFooterV2Props = {
  nextStepEvent?: () => boolean | Promise<boolean>;
  backStepEvent?: () => boolean | Promise<boolean>;
  nextLabel?: string;
  disabled?: boolean;
  showBackOnFirstStep?: boolean;
};

export function OnboardingFooterV2({
  nextStepEvent,
  backStepEvent,
  nextLabel,
  disabled,
  showBackOnFirstStep = false,
}: OnboardingFooterV2Props) {
  const { t } = useTranslation();
  const busy = useRef(false);
  const [pending, setPending] = useState(false);
  const { advanceStep, backStep, userProgress, screenNumber, subScreenNumber, registerBeforeStepBack } = useOnboarding();
  const isFirstStep = screenNumber === 1 && subScreenNumber === 0;
  const maxSection =
    userProgress.length > 0 ? Math.max(...userProgress.map((p) => p.section)) : 0;
  const lastSectionProgress = userProgress.find((p) => p.section === maxSection);
  const lastSubCount = lastSectionProgress?.screens?.length ?? 0;
  const isLastStep =
    maxSection > 0 &&
    screenNumber === maxSection &&
    lastSubCount > 0 &&
    subScreenNumber === lastSubCount - 1;

  useLayoutEffect(() => {
    if (!registerBeforeStepBack || !backStepEvent) return;
    return registerBeforeStepBack(() => busy.current ? false : backStepEvent());
  }, [registerBeforeStepBack, backStepEvent]);

  async function handleBack() {
    if (isFirstStep || busy.current) return;
    busy.current = true;
    setPending(true);
    try {
      if (backStepEvent && !(await backStepEvent())) return;
      backStep(userProgress, screenNumber, subScreenNumber);
    } finally {
      busy.current = false;
      setPending(false);
    }
  }

  async function handleNext() {
    if (disabled || busy.current) return;
    busy.current = true;
    setPending(true);
    try {
      if (nextStepEvent && !(await nextStepEvent())) return;
      advanceStep(userProgress, screenNumber, subScreenNumber);
    } finally {
      busy.current = false;
      setPending(false);
    }
  }

  return (
    <footer className="ov2-footer">
      {(!isFirstStep || showBackOnFirstStep) && (
        <ButtonV2 variant="secondary" disabled={isFirstStep || pending} onClick={handleBack}>
          {t("commonUi.back")}
        </ButtonV2>
      )}
      <ButtonV2 disabled={disabled || pending} onClick={handleNext}>
        {nextLabel || (isLastStep ? t("editFooter.complete") : t("inventory.next"))}
      </ButtonV2>
    </footer>
  );
}
